import dayjs from "dayjs";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { AdminShell } from "../components/admin/AdminShell";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { EventType } from "../types";
import { isValidMongolianPhone } from "../utils/phone";
import { formatPrice } from "../utils/payment";

function needsChildGender(event: EventType | undefined) {
  if (!event) return false;
  const text = `${event.id} ${event.title}`.toLowerCase();
  return text.includes("huuhdiin-tur") || text.includes("хүүхдийн") || text.includes("төр");
}

function noteFromForm(form: {
  quantity: string;
  giftCode: string;
  childGender: string;
  paymentMethod: string;
  adminNote: string;
}) {
  return [
    `quantity=${form.quantity}`,
    `gift=${form.giftCode}`,
    `childGender=${form.childGender}`,
    `paymentMethod=${form.paymentMethod}`,
    `manualNote=${form.adminNote.replace(/;/g, ",")}`,
  ].join("; ");
}

export function AdminManualBookingPage() {
  const [events, setEvents] = useState<EventType[]>([]);
  const [eventId, setEventId] = useState("");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [time, setTime] = useState("");
  const [availability, setAvailability] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    phone: "+976",
    email: "",
    quantity: "",
    giftCode: "",
    childGender: "",
    paymentMethod: "Утсаар авсан",
    paymentAmount: "0",
    adminNote: "",
  });
  const { toast, showToast } = useToast();

  const activeEvent = useMemo(() => events.find((event) => event.id === eventId), [eventId, events]);
  const slots = availability[date] || [];
  const showChildGender = needsChildGender(activeEvent);

  useEffect(() => {
    api.getEvents(true).then((items) => {
      const enabled = items.filter((item) => item.enabled);
      setEvents(enabled);
      setEventId(enabled[0]?.id || "");
    });
  }, []);

  useEffect(() => {
    if (!eventId) return;
    api.getAvailability(eventId, dayjs(date).format("YYYY-MM")).then((next) => {
      setAvailability(next);
      setTime("");
    });
  }, [date, eventId]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!activeEvent) return;
    if (!isValidMongolianPhone(form.phone)) {
      setError("Утасны дугаараа +97680020260 эсвэл 80020260 форматаар оруулна уу.");
      return;
    }
    if (!slots.includes(time)) {
      setError("Сонгосон цаг боломжгүй байна. Өөр цаг сонгоно уу.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const booking = await api.createManualBooking({
        eventId,
        date,
        time,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        paymentAmount: Number(form.paymentAmount || 0),
        note: noteFromForm({
          ...form,
          childGender: showChildGender ? form.childGender : "",
        }),
      });

      showToast(`${booking.date} ${booking.time} цаг гараар бүртгэгдлээ.`);
      setForm((current) => ({
        ...current,
        name: "",
        phone: "+976",
        email: "",
        quantity: "",
        giftCode: "",
        childGender: "",
        paymentAmount: "0",
        adminNote: "",
      }));
      setTime("");
      setAvailability(await api.getAvailability(eventId, dayjs(date).format("YYYY-MM")));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Захиалга хадгалахад алдаа гарлаа.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminShell active="manual" title="Гараар booking нэмэх">
      <form className="admin-manual-card" onSubmit={submit}>
        <div className="admin-card-title">
          <div>
            <h2>Утсаар авсан захиалга</h2>
            <p className="muted">QPay үүсгэхгүй, сонгосон slot-ыг шууд active booking болгож хадгална.</p>
          </div>
          <a className="secondary-link" href="/admin/bookings">Бүх захиалга харах</a>
        </div>

        <div className="manual-grid">
          <label>
            Багц *
            <select required value={eventId} onChange={(e) => setEventId(e.target.value)}>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title}
                </option>
              ))}
            </select>
          </label>
          <label>
            Өдөр *
            <input required type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label>
            Цаг *
            <select required value={time} onChange={(e) => setTime(e.target.value)}>
              <option value="">Цаг сонгох</option>
              {slots.map((slot) => (
                <option key={slot} value={slot}>
                  {slot}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="manual-summary">
          <span>Багцийн үнэ: {formatPrice(activeEvent?.price || 0)}₮</span>
          <span>Боломжтой цаг: {slots.length}</span>
        </div>

        <div className="manual-grid">
          <label>
            Нэр *
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label>
            Утас *
            <input
              required
              inputMode="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </label>
          <label>
            И-мэйл
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
        </div>

        <div className="manual-grid">
          <label>
            Зураг авах хүний тоо *
            <select required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}>
              <option value="">Сонгох</option>
              <option>1 хүн</option>
              <option>2 хүн</option>
              <option>3 хүн</option>
              <option>4 хүн</option>
              <option>5 хүн</option>
              <option>6 хүн</option>
            </select>
          </label>
          <label>
            Gift card code
            <input value={form.giftCode} onChange={(e) => setForm({ ...form, giftCode: e.target.value })} />
          </label>
          {showChildGender ? (
            <label>
              Хүү / Охин *
              <select required value={form.childGender} onChange={(e) => setForm({ ...form, childGender: e.target.value })}>
                <option value="">Сонгох</option>
                <option>Хүү</option>
                <option>Охин</option>
              </select>
            </label>
          ) : (
            <label>
              Төлбөрийн хэлбэр
              <input value={form.paymentMethod} onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })} />
            </label>
          )}
        </div>

        <div className="manual-grid compact">
          {showChildGender && (
            <label>
              Төлбөрийн хэлбэр
              <input value={form.paymentMethod} onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })} />
            </label>
          )}
          <label>
            Авсан урьдчилгаа
            <input
              min="0"
              type="number"
              value={form.paymentAmount}
              onChange={(e) => setForm({ ...form, paymentAmount: e.target.value })}
            />
          </label>
        </div>

        <label className="modal-field">
          <span>Тайлбар</span>
          <textarea
            placeholder="Жишээ: утсаар ярьж баталгаажуулсан, дансаар 20,000 авсан гэх мэт"
            value={form.adminNote}
            onChange={(e) => setForm({ ...form, adminNote: e.target.value })}
          />
        </label>

        {error && <div className="booking-error">{error}</div>}
        <button className="primary-action" disabled={saving || !time} type="submit">
          {saving ? "Хадгалж байна..." : "Гараар booking хадгалах"}
        </button>
      </form>
      <Toast message={toast} />
    </AdminShell>
  );
}
